import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users,
  ChevronLeft,
  Heart,
  MessageCircle,
  Plus,
  Send,
  Clock,
  User,
  Building2,
  HelpCircle,
  MapPin,
  Euro,
  Target,
  Briefcase,
  Crown,
  TrendingUp,
  Sparkles,
  ArrowRight,
  Star,
  Zap,
  MessageSquare,
  CheckCircle,
  Lock,
  Award
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { isNativePlatform, hasFullAccess } from '../lib/platform';

const CommunityPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [newPost, setNewPost] = useState({ 
    title: '', 
    content: '', 
    category: 'presentation',
    ville: '',
    experience: '',
    objectif: '',
    type_projet: '',
    budget: '',
    localisation_projet: '',
    situation: ''
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [viewPost, setViewPost] = useState(null); // Pour voir le post en entier
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [communityStats, setCommunityStats] = useState({
    members: 127,
    posts: 0,
    activeThisWeek: 34
  });

  const isClubMember = hasFullAccess(user?.subscription_tier);

  const categories = [
    { 
      value: 'presentation', 
      label: 'Présentez-vous', 
      icon: User, 
      color: 'bg-blue-500',
      gradient: 'from-blue-500 to-indigo-600',
      description: 'Faites connaissance avec la communauté'
    },
    { 
      value: 'projet', 
      label: 'Projet Immobilier', 
      icon: Building2, 
      color: 'bg-emerald-500',
      gradient: 'from-emerald-500 to-teal-600',
      description: 'Partagez vos projets et recevez des conseils'
    },
    { 
      value: 'questions', 
      label: 'Questions', 
      icon: HelpCircle, 
      color: 'bg-amber-500',
      gradient: 'from-amber-500 to-orange-600',
      description: 'Posez toutes vos questions'
    },
    { 
      value: 'ameliorations', 
      label: 'Améliorations App', 
      icon: Zap, 
      color: 'bg-purple-500',
      gradient: 'from-purple-500 to-violet-600',
      description: 'Proposez des améliorations pour la plateforme'
    }
  ];

  const tabs = [
    { value: 'all', label: 'Tous', icon: Sparkles },
    ...categories
  ];

  useEffect(() => {
    fetchPosts();
  }, [activeTab]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/community/posts`, {
        params: { category: activeTab !== 'all' ? activeTab : undefined },
        withCredentials: true
      });
      setPosts(response.data);
      setCommunityStats(prev => ({ ...prev, posts: response.data.length }));
    } catch (error) {
      toast.error('Erreur lors du chargement des posts');
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    let enrichedContent = newPost.content;
    
    if (newPost.category === 'presentation') {
      const parts = [];
      if (newPost.ville) parts.push(`📍 Localisation : ${newPost.ville}`);
      if (newPost.experience) parts.push(`💼 Expérience : ${newPost.experience}`);
      if (newPost.objectif) parts.push(`🎯 Objectif : ${newPost.objectif}`);
      if (parts.length > 0) {
        enrichedContent = parts.join('\n') + '\n\n' + newPost.content;
      }
    } else if (newPost.category === 'projet') {
      const parts = [];
      if (newPost.type_projet) parts.push(`🏠 Type de projet : ${newPost.type_projet}`);
      if (newPost.localisation_projet) parts.push(`📍 Localisation : ${newPost.localisation_projet}`);
      if (newPost.budget) parts.push(`💰 Budget : ${newPost.budget}`);
      if (newPost.situation) parts.push(`📋 Situation : ${newPost.situation}`);
      if (parts.length > 0) {
        enrichedContent = parts.join('\n') + '\n\n' + newPost.content;
      }
    }

    try {
      await axios.post(`${API}/community/posts`, {
        title: newPost.title,
        content: enrichedContent,
        category: newPost.category
      }, { withCredentials: true });
      toast.success('Post publié !');
      resetForm();
      setDialogOpen(false);
      fetchPosts();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const resetForm = () => {
    setNewPost({ 
      title: '', 
      content: '', 
      category: 'presentation',
      ville: '',
      experience: '',
      objectif: '',
      type_projet: '',
      budget: '',
      localisation_projet: '',
      situation: ''
    });
  };

  const likePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/community/posts/${postId}/like`, {}, { withCredentials: true });
      setPosts(posts.map(post => {
        if (post.post_id === postId) {
          return {
            ...post,
            likes: response.data.liked ? post.likes + 1 : post.likes - 1,
            liked: response.data.liked
          };
        }
        return post;
      }));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const openComments = async (post) => {
    setSelectedPost(post);
    try {
      const response = await axios.get(`${API}/community/posts/${post.post_id}/comments`, { withCredentials: true });
      setComments(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;

    try {
      await axios.post(`${API}/community/posts/${selectedPost.post_id}/comments`, {
        content: newComment
      }, { withCredentials: true });
      toast.success('Commentaire ajouté');
      setNewComment('');
      openComments(selectedPost);
      setPosts(posts.map(post => {
        if (post.post_id === selectedPost.post_id) {
          return { ...post, comments_count: post.comments_count + 1 };
        }
        return post;
      }));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch {
      return '';
    }
  };

  const getCategoryInfo = (categoryValue) => {
    return categories.find(c => c.value === categoryValue) || { label: categoryValue, color: 'bg-slate-500', icon: Users };
  };

  // Formulaire dynamique selon la catégorie
  const renderCategoryForm = () => {
    switch (newPost.category) {
      case 'presentation':
        return (
          <div className="space-y-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <div className="flex items-center gap-2 text-blue-700 font-semibold">
              <User className="w-5 h-5" />
              Présentez-vous à la communauté
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-blue-900">Votre ville / région</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
                  <Input
                    placeholder="Ex: Lyon, Île-de-France..."
                    className="pl-10 border-blue-200 focus:border-blue-400"
                    value={newPost.ville}
                    onChange={(e) => setNewPost(prev => ({ ...prev, ville: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm text-blue-900">Votre expérience</Label>
                <Select value={newPost.experience} onValueChange={(v) => setNewPost(prev => ({ ...prev, experience: v }))}>
                  <SelectTrigger className="mt-1 border-blue-200">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Débutant - Je découvre">Débutant - Je découvre</SelectItem>
                    <SelectItem value="1-2 ans d'expérience">1-2 ans d'expérience</SelectItem>
                    <SelectItem value="3-5 ans d'expérience">3-5 ans d'expérience</SelectItem>
                    <SelectItem value="Expert - Plus de 5 ans">Expert - Plus de 5 ans</SelectItem>
                    <SelectItem value="Professionnel">Professionnel de l'immobilier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm text-blue-900">Votre objectif principal</Label>
              <div className="relative mt-1">
                <Target className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
                <Input
                  placeholder="Ex: Construire un patrimoine, générer des revenus passifs..."
                  className="pl-10 border-blue-200"
                  value={newPost.objectif}
                  onChange={(e) => setNewPost(prev => ({ ...prev, objectif: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm text-blue-900">Votre message *</Label>
              <Textarea
                placeholder="Présentez-vous en quelques lignes..."
                rows={4}
                className="mt-1 border-blue-200"
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
          </div>
        );

      case 'projet':
        return (
          <div className="space-y-4 p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold">
              <Building2 className="w-5 h-5" />
              Décrivez votre projet
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-emerald-900">Type de projet</Label>
                <Select value={newPost.type_projet} onValueChange={(v) => setNewPost(prev => ({ ...prev, type_projet: v }))}>
                  <SelectTrigger className="mt-1 border-emerald-200">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Premier achat locatif">Premier achat locatif</SelectItem>
                    <SelectItem value="Résidence principale">Résidence principale</SelectItem>
                    <SelectItem value="Investissement locatif">Investissement locatif</SelectItem>
                    <SelectItem value="Location courte durée">Location courte durée</SelectItem>
                    <SelectItem value="Achat-revente / MDB">Achat-revente / MDB</SelectItem>
                    <SelectItem value="Immeuble de rapport">Immeuble de rapport</SelectItem>
                    <SelectItem value="Division / Colocation">Division / Colocation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-emerald-900">Localisation visée</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
                  <Input
                    placeholder="Ex: Bordeaux..."
                    className="pl-10 border-emerald-200"
                    value={newPost.localisation_projet}
                    onChange={(e) => setNewPost(prev => ({ ...prev, localisation_projet: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm text-emerald-900">Budget</Label>
                <div className="relative mt-1">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400" />
                  <Input
                    placeholder="Ex: 150 000 €"
                    className="pl-10 border-emerald-200"
                    value={newPost.budget}
                    onChange={(e) => setNewPost(prev => ({ ...prev, budget: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm text-emerald-900">Où en êtes-vous ?</Label>
                <Select value={newPost.situation} onValueChange={(v) => setNewPost(prev => ({ ...prev, situation: v }))}>
                  <SelectTrigger className="mt-1 border-emerald-200">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Je réfléchis">Je réfléchis encore</SelectItem>
                    <SelectItem value="En recherche">En recherche active</SelectItem>
                    <SelectItem value="Négociation">Bien trouvé, en négociation</SelectItem>
                    <SelectItem value="Offre acceptée">Offre acceptée</SelectItem>
                    <SelectItem value="Travaux">Travaux en cours</SelectItem>
                    <SelectItem value="Terminé">Projet terminé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm text-emerald-900">Décrivez votre projet *</Label>
              <Textarea
                placeholder="Expliquez votre projet en détail..."
                rows={4}
                className="mt-1 border-emerald-200"
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
          </div>
        );

      case 'questions':
        return (
          <div className="space-y-4 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100">
            <div className="flex items-center gap-2 text-amber-700 font-semibold">
              <HelpCircle className="w-5 h-5" />
              Posez votre question
            </div>
            <div>
              <Label className="text-sm text-amber-900">Votre question *</Label>
              <Textarea
                placeholder="Posez votre question en détail..."
                rows={6}
                className="mt-1 border-amber-200"
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
            <p className="text-sm text-amber-600 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              Conseil : Plus vous donnez de contexte, meilleures seront les réponses !
            </p>
          </div>
        );

      case 'ameliorations':
        return (
          <div className="space-y-4 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100">
            <div className="flex items-center gap-2 text-green-700 font-semibold">
              <Zap className="w-5 h-5" />
              Proposez une amélioration
            </div>
            <div>
              <Label className="text-sm text-green-900">Votre suggestion *</Label>
              <Textarea
                placeholder="Décrivez l'amélioration que vous souhaitez voir sur l'application..."
                rows={6}
                className="mt-1 border-green-200"
                value={newPost.content}
                onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1]">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Tableau de bord
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" />
              </Link>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0D47A1] hover:bg-[#0A367A] shadow-lg shadow-[#0D47A1]/20" data-testid="new-post-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouveau post
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl">Créer un post</DialogTitle>
                  <DialogDescription>
                    Partagez avec la communauté d'investisseurs
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-5 mt-4">
                  {/* Category Selection */}
                  <div>
                    <Label className="text-sm font-semibold mb-3 block">Type de publication</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {categories.map((cat) => (
                        <button
                          key={cat.value}
                          onClick={() => setNewPost(prev => ({ ...prev, category: cat.value }))}
                          className={`p-4 rounded-xl border-2 transition-all text-left group ${
                            newPost.category === cat.value
                              ? 'border-[#0D47A1] bg-[#0D47A1]/5 shadow-md'
                              : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${cat.gradient} flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                            <cat.icon className="w-5 h-5 text-white" />
                          </div>
                          <p className={`font-semibold text-sm ${newPost.category === cat.value ? 'text-[#0D47A1]' : 'text-slate-900'}`}>
                            {cat.label}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">{cat.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <Label className="text-sm font-semibold">Titre *</Label>
                    <Input
                      placeholder={
                        newPost.category === 'presentation' ? "Ex: Bonjour, je suis Jean de Lyon !" :
                        newPost.category === 'projet' ? "Ex: Mon premier investissement à Bordeaux" :
                        "Ex: Comment calculer la rentabilité nette ?"
                      }
                      className="mt-2 h-11"
                      value={newPost.title}
                      onChange={(e) => setNewPost(prev => ({ ...prev, title: e.target.value }))}
                      data-testid="post-title-input"
                    />
                  </div>

                  {renderCategoryForm()}

                  <Button
                    onClick={createPost}
                    className="w-full h-11 bg-[#0D47A1] hover:bg-[#0A367A] shadow-lg"
                    data-testid="submit-post-btn"
                  >
                    Publier mon post
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </nav>

      {/* Hero Section with Stats */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D47A1] via-[#1565C0] to-[#0D47A1]" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#00B050] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/90 text-sm mb-4">
              <Users className="w-4 h-4" />
              Communauté d'investisseurs
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Rejoignez la communauté 10M Investor
            </h1>
            <p className="text-blue-100 text-lg max-w-2xl mx-auto">
              Échangez avec des investisseurs passionnés, partagez vos projets et apprenez des expériences de chacun
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
              <div className="text-3xl font-bold text-white">{communityStats.members}+</div>
              <div className="text-blue-200 text-sm mt-1">Membres actifs</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
              <div className="text-3xl font-bold text-white">{communityStats.posts}</div>
              <div className="text-blue-200 text-sm mt-1">Publications</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20">
              <div className="text-3xl font-bold text-white">{communityStats.activeThisWeek}</div>
              <div className="text-blue-200 text-sm mt-1">Actifs cette semaine</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
          {/* Feed Column */}
          <div>
            {/* Category Tabs */}
            <div className="mb-6 flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const IconComponent = tab.icon || Users;
                const isActive = activeTab === tab.value;
                return (
                  <button
                    key={tab.value}
                    onClick={() => setActiveTab(tab.value)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      isActive
                        ? 'bg-[#0D47A1] text-white shadow-lg shadow-[#0D47A1]/20'
                        : 'bg-white border border-slate-200 text-slate-600 hover:border-[#0D47A1] hover:text-[#0D47A1]'
                    }`}
                    data-testid={`tab-${tab.value}`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Posts List */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="border-slate-200 overflow-hidden">
                    <CardContent className="p-6">
                      <div className="animate-pulse">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 bg-slate-200 rounded-full" />
                          <div className="flex-1">
                            <div className="h-4 w-32 bg-slate-200 rounded mb-2" />
                            <div className="h-3 w-24 bg-slate-200 rounded" />
                          </div>
                        </div>
                        <div className="h-5 w-3/4 bg-slate-200 rounded mb-3" />
                        <div className="h-4 w-full bg-slate-200 rounded mb-2" />
                        <div className="h-4 w-2/3 bg-slate-200 rounded" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <Card className="border-slate-200 border-dashed">
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun post pour le moment</h3>
                  <p className="text-slate-500 mb-6">Soyez le premier à partager avec la communauté !</p>
                  <Button onClick={() => setDialogOpen(true)} className="bg-[#0D47A1] hover:bg-[#0A367A]">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer le premier post
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map((post, index) => {
                  const catInfo = getCategoryInfo(post.category);
                  const IconComponent = catInfo.icon;
                  return (
                    <Card 
                      key={post.post_id} 
                      className="border-slate-200 hover:border-[#0D47A1]/30 hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer"
                      style={{ animationDelay: `${index * 50}ms` }}
                      data-testid={`post-card-${post.post_id}`}
                      onClick={() => setViewPost(post)}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          {/* Avatar */}
                          <div className="relative flex-shrink-0">
                            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${catInfo.gradient} flex items-center justify-center text-white font-bold text-lg shadow-md`}>
                              {post.author_name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            {post.is_club_member && (
                              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center border-2 border-white">
                                <Crown className="w-3 h-3 text-amber-900" />
                              </div>
                            )}
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap gap-2 mb-2">
                              <span className="font-semibold text-slate-900">{post.author_name}</span>
                              {post.is_club_member && (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                                  <Crown className="w-3 h-3 mr-1" />
                                  Club
                                </Badge>
                              )}
                              <span className="text-slate-400 text-sm flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {formatDate(post.created_at)}
                              </span>
                            </div>
                            
                            <Badge className={`${catInfo.color} text-white mb-3`}>
                              <IconComponent className="w-3 h-3 mr-1" />
                              {catInfo.label}
                            </Badge>
                            
                            <h3 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#0D47A1] transition-colors">
                              {post.title}
                            </h3>
                            <p className="text-slate-600 whitespace-pre-wrap line-clamp-4">{post.content}</p>
                            
                            {/* Lire la suite indicator */}
                            {post.content && post.content.length > 200 && (
                              <button className="text-[#0D47A1] text-sm font-medium mt-2 hover:underline">
                                Lire la suite...
                              </button>
                            )}
                            
                            {/* Actions */}
                            <div className="flex items-center gap-6 mt-4 pt-4 border-t border-slate-100">
                              <button
                                onClick={(e) => { e.stopPropagation(); likePost(post.post_id); }}
                                className={`flex items-center gap-2 transition-colors ${
                                  post.liked ? 'text-red-500' : 'text-slate-500 hover:text-red-500'
                                }`}
                                data-testid={`like-btn-${post.post_id}`}
                              >
                                <Heart className={`h-5 w-5 ${post.liked ? 'fill-current' : ''}`} />
                                <span className="font-medium">{post.likes}</span>
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); openComments(post); }}
                                className="flex items-center gap-2 text-slate-500 hover:text-[#0D47A1] transition-colors"
                                data-testid={`comments-btn-${post.post_id}`}
                              >
                                <MessageCircle className="h-5 w-5" />
                                <span className="font-medium">{post.comments_count}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Club CTA for Free Users */}
            {!isClubMember && (
              <Card className="border-0 bg-gradient-to-br from-amber-50 via-amber-100/50 to-orange-50 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/30 rounded-full blur-2xl" />
                <CardContent className="p-6 relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">Rejoignez le Club</h3>
                      <p className="text-sm text-amber-700">Accès exclusif</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {[
                      { icon: MessageSquare, text: "Messagerie privée entre membres" },
                      { icon: Users, text: "Annuaire des investisseurs" },
                      { icon: Building2, text: "Biens exclusifs du Club" },
                      { icon: Award, text: "Badge membre Club" },
                      { icon: Zap, text: "Agent IA illimité" }
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-slate-700">
                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <item.icon className="w-4 h-4 text-amber-600" />
                        </div>
                        <span className="text-sm font-medium">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {!isNativePlatform() ? (
                  <Link to="/pricing">
                    <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg" data-testid="upgrade-cta-btn">
                      Découvrir le Club
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  ) : (
                    <p className="text-center text-sm text-slate-500 font-medium py-2">Bientôt disponible</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Community Guidelines */}
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-[#0D47A1]" />
                  Règles de la communauté
                </h3>
                <ul className="space-y-3">
                  {[
                    "Restez respectueux et bienveillant",
                    "Partagez vos expériences sincères",
                    "L'auto-promotion est interdite sauf pour les partenaires pros du Club",
                    "Aidez les autres membres"
                  ].map((rule, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="w-4 h-4 text-[#00B050] mt-0.5 flex-shrink-0" />
                      {rule}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Quick Links for Club Members */}
            {isClubMember && (
              <Card className="border-slate-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    Accès Club
                  </h3>
                  <div className="space-y-2">
                    <Link to="/directory">
                      <Button variant="ghost" className="w-full justify-start text-slate-700 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5">
                        <Users className="w-4 h-4 mr-3" />
                        Annuaire des membres
                      </Button>
                    </Link>
                    <Link to="/messages">
                      <Button variant="ghost" className="w-full justify-start text-slate-700 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5">
                        <MessageSquare className="w-4 h-4 mr-3" />
                        Messagerie privée
                      </Button>
                    </Link>
                    <Link to="/properties">
                      <Button variant="ghost" className="w-full justify-start text-slate-700 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5">
                        <Building2 className="w-4 h-4 mr-3" />
                        Biens du Club
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trending Topics */}
            <Card className="border-slate-200">
              <CardContent className="p-5">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#00B050]" />
                  Sujets populaires
                </h3>
                <div className="flex flex-wrap gap-2">
                  {["LMNP", "Rentabilité", "Fiscalité", "Travaux", "Financement", "Colocation"].map((topic) => (
                    <Badge key={topic} variant="outline" className="cursor-pointer hover:bg-slate-100 transition-colors">
                      #{topic}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* View Post Dialog */}
      <Dialog open={!!viewPost} onOpenChange={() => setViewPost(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {viewPost && (() => {
            const catInfo = getCategoryInfo(viewPost.category);
            const IconComponent = catInfo.icon;
            return (
              <>
                <DialogHeader className="pb-4 border-b">
                  <div className="flex items-start gap-4">
                    <div className="relative flex-shrink-0">
                      <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${catInfo.gradient} flex items-center justify-center text-white font-bold text-xl shadow-md`}>
                        {viewPost.author_name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      {viewPost.is_club_member && (
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center border-2 border-white">
                          <Crown className="w-3.5 h-3.5 text-amber-900" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-1">
                        <span className="font-bold text-slate-900 text-lg">{viewPost.author_name}</span>
                        {viewPost.is_club_member && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                            <Crown className="w-3 h-3 mr-1" />
                            Club
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-slate-500 text-sm">
                        <span className="flex items-center">
                          <Clock className="h-3.5 w-3.5 mr-1" />
                          {formatDate(viewPost.created_at)}
                        </span>
                        <Badge className={`${catInfo.color} text-white`}>
                          <IconComponent className="w-3 h-3 mr-1" />
                          {catInfo.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="flex-1 overflow-y-auto py-6">
                  <DialogTitle className="text-xl font-bold text-slate-900 mb-4">
                    {viewPost.title}
                  </DialogTitle>
                  <div className="prose prose-slate max-w-none">
                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {viewPost.content}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => { likePost(viewPost.post_id); }}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        viewPost.liked 
                          ? 'bg-red-50 text-red-500' 
                          : 'bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-500'
                      }`}
                    >
                      <Heart className={`h-5 w-5 ${viewPost.liked ? 'fill-current' : ''}`} />
                      <span className="font-medium">{viewPost.likes}</span>
                    </button>
                    <button
                      onClick={() => { setViewPost(null); openComments(viewPost); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-[#0D47A1]/10 hover:text-[#0D47A1] transition-colors"
                    >
                      <MessageCircle className="h-5 w-5" />
                      <span className="font-medium">{viewPost.comments_count} commentaires</span>
                    </button>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => setViewPost(null)}
                  >
                    Fermer
                  </Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#0D47A1]" />
              Commentaires
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {comments.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucun commentaire pour le moment</p>
                <p className="text-sm text-slate-400">Soyez le premier à commenter !</p>
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.comment_id} className="flex gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-600 font-semibold text-sm flex-shrink-0">
                    {comment.author_name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900 text-sm">{comment.author_name}</span>
                      <span className="text-xs text-slate-400">{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="text-slate-600 text-sm">{comment.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2 pt-4 border-t">
            <Input
              placeholder="Votre commentaire..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addComment()}
              className="flex-1"
            />
            <Button onClick={addComment} className="bg-[#0D47A1] hover:bg-[#0A367A]">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityPage;
